import React from "react";
import S from "./FormSuggestions.styled";
import { MainSection } from "@/styles/global";
import axios from "axios";
import FormSuccess from "@/components/global-components/FormSuccess";
import { useForm } from "react-hook-form";

export interface FormSuggestionsProps {}

type FormData = {
  dovas_request_category: string;
  ansokanFor: string;
  namn: string;
  feedbackTyp: string;
  beskrivning: string;
  ovrigt?: string;
  email: string;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  dovas_request_category: "Förslag",
  ansokanFor: "Verksamhet",
  namn: "",
  feedbackTyp: "",
  beskrivning: "",
  email: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const FormSuggestions: React.FC<FormSuggestionsProps> = () => {
  const [active, setActive] = React.useState("Verksamhet");
  const [formState, setFormState] = React.useState(emptyFormState());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  const submitForm = handleSubmit(() => {
    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));
    axios
      .post(process.env.GATSBY_CONTACT_URL || "", {
        ...formState.data,
      })
      .then((res) => {
        if (!res.data.success) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            error: res.data.error_msg,
          }));
        } else {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      });
  });

  return (
    <MainSection>
      <S.Inner>
        {formState.success ? (
          <FormSuccess feedback />
        ) : (
          <S.FormWrapper>
            <S.H4>Lämna feedback till SDF:s verksamhet</S.H4>
            <S.Wrapper>
              <S.H6>Det gäller</S.H6>
              <S.FlexWrapper>
                <S.Button
                  className={active === "Verksamhet" ? "active" : ""}
                  onClick={() => {
                    updateFormProperty("ansokanFor", "Verksamhet");
                    setActive("Verksamhet");
                  }}
                >
                  Verksamhet
                </S.Button>
                <S.Button
                  className={active === "Föreningen" ? "active" : ""}
                  onClick={() => {
                    updateFormProperty("ansokanFor", "Föreningen");
                    setActive("Föreningen");
                  }}
                >
                  Föreningen
                </S.Button>
              </S.FlexWrapper>
            </S.Wrapper>
            <S.Wrapper>
              <S.DetailsLarge>Obligatoriska fält</S.DetailsLarge>
            </S.Wrapper>
            <S.Form onSubmit={submitForm}>
              <S.Wrapper>
                <S.Label htmlFor="namn">Ditt namn</S.Label>
                <S.Input
                  type="text"
                  {...register("namn", { required: true })}
                  className={errors.namn ? "error" : ""}
                  placeholder="Ditt namn"
                  onChange={(e) => updateFormProperty("namn", e.target.value)}
                />
                {errors.namn && <S.Error>Namn är obligatoriskt</S.Error>}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="email">E-post</S.Label>
                <S.Input
                  type="email"
                  {...register("email", { required: true })}
                  className={errors.email ? "error" : ""}
                  placeholder="E-postadress"
                  onChange={(e) => updateFormProperty("email", e.target.value)}
                />
                {errors.email && <S.Error>E-post är obligatoriskt</S.Error>}
              </S.Wrapper>
              <S.Wrapper>
                <div>
                  <S.RadioButton
                    type="radio"
                    {...register("feedbackTyp", { required: true })}
                    className={
                      errors.feedbackTyp ? "error custom-radio" : "custom-radio"
                    }
                    value="Feedback på något som ägt rum"
                    onChange={(e) =>
                      updateFormProperty("feedbackTyp", e.target.value)
                    }
                  />
                  <S.SmallLabel htmlFor="feedbackTyp">
                    Lämna feedback på något som hänt/ägt rum
                  </S.SmallLabel>
                </div>
              </S.Wrapper>
              <S.Wrapper>
                <div>
                  <S.RadioButton
                    type="radio"
                    className={
                      errors.feedbackTyp ? "error custom-radio" : "custom-radio"
                    }
                    {...register("feedbackTyp", { required: true })}
                    value="Feedback på kommande aktivitet"
                    onChange={(e) =>
                      updateFormProperty("feedbackTyp", e.target.value)
                    }
                  />
                  <S.SmallLabel htmlFor="feedbackTyp">
                    Lämna förslag på kommande aktiviteter
                  </S.SmallLabel>
                </div>
                {errors.feedbackTyp && <S.Error>Välj ett alternativ</S.Error>}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="beskrivning">
                  Beskrivning av din förslag eller feedback
                </S.Label>
                <S.SmallLabel>
                  Tänk på att vara så utförlig som möjligt
                </S.SmallLabel>
                <S.TextArea
                  {...register("beskrivning", { required: true })}
                  className={errors.beskrivning ? "error" : ""}
                  placeholder="Beskrivning av förslag eller feedback"
                  onChange={(e) =>
                    updateFormProperty("beskrivning", e.target.value)
                  }
                />
                {errors.beskrivning && (
                  <S.Error>Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="ovrigt" className="not-required">
                  Övrig information
                </S.Label>
                <S.TextArea
                  name="ovrigt"
                  placeholder="Övrig information"
                  onChange={(e) => updateFormProperty("ovrigt", e.target.value)}
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.CheckboxContainer>
                  <S.CheckboxWrapper>
                    <S.Checkbox
                      type="checkbox"
                      {...register("accept", { required: true })}
                      className={errors.accept ? "error" : ""}
                    />
                    <svg
                      width="15"
                      height="13"
                      viewBox="0 0 15 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.1667 2.52002L5.37504 10.3117L1.83337 6.77002"
                        stroke="#281E78"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.CheckboxWrapper>
                  <S.SmallLabel htmlFor="accept">
                    Jag godkänner Stockholm Dövas Förenings{" "}
                    <a href="/">integritetspolicy.</a>
                  </S.SmallLabel>
                </S.CheckboxContainer>
              </S.Wrapper>
              <S.Wrapper>
                <S.SubmitButton type="submit">Skicka</S.SubmitButton>
                {formState.error && (
                  <S.FormError>
                    Något gick fel, vänligen försök igen.
                  </S.FormError>
                )}
              </S.Wrapper>
            </S.Form>
          </S.FormWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default FormSuggestions;
